import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ImagePlus, Loader2, GitCompare, Trash2, Sparkles } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { supabase } from '../../../lib/supabaseClient'
import { useAuth } from '../../../contexts/AuthContext'
import { cn } from '../../../lib/utils'

// Private body-progress album + AI diff. Photos live in the PRIVATE
// progress-photos bucket (own-folder RLS); thumbnails render through
// short-lived signed URLs. Pick any two shots → progress-photo-diff
// compares them with Gemini vision + weight context and the
// Egyptian-Arabic narrative lands below (and in progress_photo_diffs).

const POSES = ['front', 'side', 'back']
const BUCKET = 'progress-photos'

export default function ProgressAlbum() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const fileRef = useRef(null)

  const [photos, setPhotos] = useState([])
  const [urls, setUrls] = useState({})
  const [pose, setPose] = useState('front')
  const [uploading, setUploading] = useState(false)
  const [selected, setSelected] = useState([])
  const [comparing, setComparing] = useState(false)
  const [diff, setDiff] = useState(null)

  const refetch = useCallback(async () => {
    if (!user) return
    const { data } = await supabase
      .from('progress_photos')
      .select('id, date, pose, storage_path')
      .eq('user_id', user.id)
      .order('date', { ascending: false })
      .limit(60)
    const rows = data || []
    setPhotos(rows)
    if (rows.length) {
      const { data: signed } = await supabase.storage
        .from(BUCKET)
        .createSignedUrls(rows.map((r) => r.storage_path), 3600)
      const map = {}
      for (const s of signed || []) {
        if (s.signedUrl) map[s.path] = s.signedUrl
      }
      setUrls(map)
    }
    const { data: lastDiff } = await supabase
      .from('progress_photo_diffs')
      .select('narrative, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
    if (lastDiff?.[0]) setDiff((d) => d || { narrative: lastDiff[0].narrative })
  }, [user])

  useEffect(() => { refetch() }, [refetch])

  const upload = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !user || uploading) return
    setUploading(true)
    try {
      const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg'
      const path = `${user.id}/${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type })
      if (upErr) throw upErr
      const today = new Date().toLocaleDateString('en-CA')
      const { error: insErr } = await supabase
        .from('progress_photos')
        .insert({ user_id: user.id, date: today, pose, storage_path: path })
      if (insErr) throw insErr
      await refetch()
    } catch { /* surfaced by missing thumbnail; keep quiet */ }
    setUploading(false)
  }

  const toggleSelect = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= 2) return [prev[1], id]
      return [...prev, id]
    })
  }

  const compare = async () => {
    if (selected.length !== 2 || comparing) return
    setComparing(true)
    const { data, error } = await supabase.functions.invoke('progress-photo-diff', {
      body: { from_id: selected[0], to_id: selected[1] },
    })
    setComparing(false)
    setDiff(error || !data?.narrative ? { failed: true } : data)
  }

  const removePhoto = async (photo) => {
    await supabase.storage.from(BUCKET).remove([photo.storage_path])
    await supabase.from('progress_photos').delete().eq('id', photo.id)
    setSelected((prev) => prev.filter((x) => x !== photo.id))
    refetch()
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="flex-1 min-w-0 text-sm font-semibold flex items-center gap-1.5 truncate" dir="auto">
            <GitCompare className="w-4 h-4 text-app-primary-500" />
            {t('physique.album.title')}
          </h2>
          <div className="flex gap-1">
            {POSES.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPose(p)}
                className={cn(
                  'px-2 py-1 rounded-md text-[11px] font-semibold transition-colors',
                  pose === p ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground',
                )}
                dir="auto"
              >
                {t(`physique.album.pose.${p}`)}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          </Button>
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" />

        {photos.length === 0 && (
          <p className="text-xs text-muted-foreground" dir="auto">{t('physique.album.empty')}</p>
        )}

        {photos.length > 0 && (
          <>
            <div className="grid grid-cols-4 gap-2" dir="ltr">
              {photos.map((p) => {
                const sel = selected.indexOf(p.id)
                return (
                  <div key={p.id} className="relative">
                    <button
                      type="button"
                      onClick={() => toggleSelect(p.id)}
                      className={cn(
                        'block w-full aspect-[3/4] rounded-lg overflow-hidden border-2 transition-colors',
                        sel >= 0 ? 'border-app-primary-500' : 'border-transparent',
                      )}
                    >
                      {urls[p.storage_path]
                        ? <img src={urls[p.storage_path]} alt="" className="w-full h-full object-cover" loading="lazy" />
                        : <div className="w-full h-full bg-muted" />}
                    </button>
                    {sel >= 0 && (
                      <span className="absolute top-1 start-1 w-5 h-5 rounded-full bg-app-primary-500 text-white text-[11px] font-bold inline-flex items-center justify-center">
                        {sel + 1}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(p)}
                      aria-label={t('common.delete')}
                      className="absolute top-1 end-1 p-1 rounded-md bg-black/50 text-white"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                    <p className="text-[9px] text-muted-foreground text-center mt-0.5" dir="ltr">{p.date}</p>
                  </div>
                )
              })}
            </div>

            <Button onClick={compare} disabled={selected.length !== 2 || comparing} className="w-full">
              {comparing
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><Sparkles className="w-4 h-4 me-1.5" />{t('physique.album.compare')}</>}
            </Button>
          </>
        )}

        {diff?.failed && (
          <p className="text-xs text-red-500" dir="auto">{t('physique.album.failed')}</p>
        )}
        {diff?.narrative && (
          <div className="rounded-md border border-app-primary-500/30 bg-app-primary-500/5 p-3" dir="auto">
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{diff.narrative}</p>
            {diff.from && (
              <p className="text-[10px] text-muted-foreground mt-1.5" dir="ltr">{diff.from} → {diff.to}</p>
            )}
          </div>
        )}

      </CardContent>
    </Card>
  )
}
